// src/components/layout/MobileSystemMenu.jsx
import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

// 웹 SystemLayout의 섹션 정의를 모바일용으로 재구성 (권한 포함)
const allSections = [
  {
    title: "내 정보",
    items: [
      { path: "user", label: "내 정보" },
      { path: "mypage", label: "마이페이지" },
    ],
  },
  {
    title: "사용자 관리",
    permission: "isStaff",
    items: [
      { path: "user/add", label: "사용자 등록" },
      { path: "user/list", label: "사용자 목록" },
    ],
  },
  {
    title: "조직 관리",
    permission: "isStaff",
    items: [
      { path: "user/company", label: "회사 관리" },
      { path: "company/add", label: "회사 등록" },
      { path: "user/pcp", label: "참여 역할 관리" },
    ],
  },
  {
    title: "직무·직위 관리",
    permission: "isStaff",
    items: [
      { path: "user/department", label: "직무 / 소속" },
      { path: "user/position", label: "직급 / 역할" },
    ],
  },
  {
    title: "권한·기타",
    permission: "isStaff",
    items: [{ path: "grant", label: "권한 관리" }],
  },
  {
    title: "프로젝트 관리",
    permission: "isSuperuser",
    items: [
      { path: "modify", label: "프로젝트 수정" },
      { path: "select-project/", label: "전체 프로젝트", isAbsolute: true },
    ],
  },
];

export default function MobileSystemMenu({ onBack, closeMenu }) {
  const { id: projectId } = useParams();
  const navigate = useNavigate();

  // 권한 체크
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const toBool = (v) => v === true || v === "true";
  const isStaff = toBool(user?.is_staff);
  const isSuperuser = toBool(user?.is_superuser);

  // 권한에 따라 섹션 필터링
  const filteredSections = useMemo(() => {
    return allSections.filter((section) => {
      if (!section.permission) return true;
      if (isSuperuser) return true;
      if (section.permission === "isStaff" && isStaff) return true;
      if (section.permission === "isSuperuser" && isSuperuser) return true;
      return false;
    });
  }, [isStaff, isSuperuser]);

  const handleClick = (item) => {
    if (!projectId && !item.isAbsolute) {
      navigate("/select-project");
      return;
    }

    if (item.isAbsolute) {
      // 절대 경로로 이동
      navigate(`/${item.path}`);
    } else {
      navigate(`/project/${projectId}/system/${item.path}`);
    }
    closeMenu();
  };

  return (
    <div className="w-full h-full flex flex-col">
      {/* 뒤로가기 헤더 */}
      <div className="flex items-center gap-2 p-3 border-b border-white/10">
        <button
          onClick={onBack}
          className="p-1.5 rounded-full hover:bg-white/10"
          aria-label="뒤로가기"
        >
          <ChevronLeft size={20} />
        </button>
        <h3 className="text-base font-semibold">마이페이지 / 설정</h3>
      </div>

      {/* 메뉴 목록 */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {filteredSections.map((section) => (
          <div key={section.title} className="bg-white/5 rounded-lg p-2">
            <div className="text-[11px] font-semibold text-gray-300 mb-1.5 px-1">
              {section.title}
            </div>
            <div className="space-y-1">
              {section.items.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => handleClick(item)}
                  className="w-full text-left px-3 py-2.5 rounded-md bg-white/5 hover:bg-white/15 text-sm text-gray-100 transition-colors"
                >
                  <span className="truncate">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
